import CaretLeftOutlined from '@ant-design/icons/CaretLeftOutlined';
import CaretRightOutlined from '@ant-design/icons/CaretRightOutlined';
import cx from '@vindr/utils/helpers/cx';
import { Layout, SiderProps } from 'antd';
import React, { CSSProperties, useEffect, useState } from 'react';

const { Sider } = Layout;

const baseClasses =
  'transition-all duration-300 ease-in-out h-full bg-secondary-lighter dark:bg-secondary-dark dark:border-gray-800 justify-start box-content flex flex-col text-black dark:text-white';

type SidePanelProp = {
  side?: 'left' | 'right';
  className?: string;
  children: React.ReactNode;
  top?: string;
  width?: SiderProps['width'];
  showTrigger?: boolean;
  showCloseTrigger?: boolean;
  showOpenTrigger?: boolean;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onChange?: (isCollapsed: boolean) => void;
  sideOverlayClassName?: string;
};

const SidePanel = ({
  side = 'left',
  className,
  children,
  width = 140,
  top = '50%',
  showTrigger = true,
  showCloseTrigger = true,
  showOpenTrigger = true,
  collapsed = null,
  defaultCollapsed = window.innerWidth < 640,
  onChange = null,
  sideOverlayClassName,
}: SidePanelProp): JSX.Element => {
  const [panelCollapsed, setPanelCollapsed] = useState(collapsed || defaultCollapsed);

  useEffect(() => {
    if (typeof collapsed == 'boolean') {
      setPanelCollapsed(collapsed);
    }
  }, [collapsed]);

  const trigger = (
    <div className="flex h-14 w-2.5 cursor-pointer items-center justify-center rounded-r-md bg-secondary-main max-sm:w-5">
      {panelCollapsed ? (
        <CaretRightOutlined className="text-lg" />
      ) : (
        <CaretLeftOutlined className="text-lg" />
      )}
    </div>
  );

  const rightTrigger = (
    <div className="flex h-14 w-2.5 cursor-pointer items-center justify-center rounded-l-md bg-secondary-main max-sm:w-5">
      {panelCollapsed ? (
        <CaretLeftOutlined className="text-lg" />
      ) : (
        <CaretRightOutlined className="text-lg" />
      )}
    </div>
  );

  const leftTriggerStyle: CSSProperties = {
    width: 'auto',
    height: 'auto',
    position: 'absolute',
    left: panelCollapsed ? 0 : width,
    transition: '0.25s ease-in-out',
    top,
    zIndex: 1,
    borderTopRightRadius: '0.375rem',
    borderBottomRightRadius: '0.375rem',
    overflow: 'hidden',
  };

  const rightTriggerStyle: CSSProperties = {
    width: 'auto',
    height: 'auto',
    position: 'absolute',
    right: panelCollapsed ? 0 : width,
    transition: '0.25s ease-in-out',
    top,
    zIndex: 1,
    borderTopLeftRadius: '0.375rem',
    borderBottomLeftRadius: '0.375rem',
    overflow: 'hidden',
  };

  const handleCollapse = () => {
    onChange?.(true);
    setPanelCollapsed(true);
  };

  return (
    <>
      {!panelCollapsed && window.innerWidth < 640 ? (
        <div
          className={cx(
            'sider-overlay overlay fixed z-10 h-full w-full bg-overlay',
            sideOverlayClassName
          )}
          onClick={handleCollapse}
        ></div>
      ) : null}
      <Sider
        collapsible
        collapsed={panelCollapsed}
        onCollapse={(value) => {
          if (!value) {
            // close other first
            const siderOverlay = document.getElementsByClassName(
              'sider-overlay'
            )?.[0] as HTMLElement;

            siderOverlay && siderOverlay.click();
          }
          setPanelCollapsed(value);
          onChange?.(value);
        }}
        className={cx(
          baseClasses,
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
          'top-10 tablet:relative z-[20] max-[639px]:fixed',
          className
        )}
        trigger={
          showTrigger &&
          ((panelCollapsed && showOpenTrigger) || // panel is close
            (!panelCollapsed && showCloseTrigger)) // panel is open
            ? side === 'left'
              ? trigger
              : rightTrigger
            : null
        }
        collapsedWidth={0}
        width={width}
        zeroWidthTriggerStyle={side === 'left' ? leftTriggerStyle : rightTriggerStyle}
      >
        {children}
      </Sider>
    </>
  );
};

export default SidePanel;
